import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/i18n/index";
import { useWebSocket } from "@/contexts/WebSocketContext";
import ProfessionalLayout from "@/components/layout/ProfessionalLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { formatCancerDisplay } from "@/components/shared/OncologyContext";
import api from "@/api";
import { toast } from "sonner";
import {
  AlertTriangle, Users, Activity, ChevronRight, Clock, RefreshCw,
  Heart, Eye, CheckCircle, Building2, X, Hospital, ArrowUpRight, Pill, Phone, ClipboardList, Calendar
} from "lucide-react";
import { getItems } from "@/utils/apiHelpers";
import { getScheduledCheckinExpectation } from "@/utils/checkinFrequency";

import PreChemoBilanEncart from "@/components/clinical/PreChemoBilanEncart";
import ProfessionalTodayActions from "@/components/professional/ProfessionalTodayActions";
import EscalationFollowupModal from "@/components/clinical/EscalationFollowupModal";

const RESOLUTION_OUTCOME_STYLES = {
  rose: { selected: "border-rose-500 bg-rose-50", iconBg: "bg-rose-100" },
  emerald: { selected: "border-emerald-500 bg-emerald-50", iconBg: "bg-emerald-100" },
  sky: { selected: "border-sky-500 bg-sky-50", iconBg: "bg-sky-100" },
  slate: { selected: "border-slate-500 bg-slate-100", iconBg: "bg-slate-200" },
};

const getResolutionOutcomeStyles = (color) => RESOLUTION_OUTCOME_STYLES[color] || RESOLUTION_OUTCOME_STYLES.sky;

export default function NurseDashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { on } = useWebSocket();
  const [alerts, setAlerts] = useState([]);
  const [patientsByCategory, setPatientsByCategory] = useState({
    noCheckin: [],
    critical: [],
    toMonitor: [],
    hospitalized: []
  });
  // Phase point 4 — RDV du jour (patients dont je suis IDE référent)
  const [todayAppts, setTodayAppts] = useState([]);
  const [oralPatients, setOralPatients] = useState([]);
  const [proFollowup, setProFollowup] = useState({ totalOpen: 0, late: 0, dueNow: 0 });
  const [loading, setLoading] = useState(true);
  
  // Alert resolution modal state
  const [showResolveModal, setShowResolveModal] = useState(null);
  const [escalationResult, setEscalationResult] = useState(null);
  const [resolveAction, setResolveAction] = useState("resolve"); // "resolve" or "escalate"
  const [resolveOutcome, setResolveOutcome] = useState("");
  const [resolveNotes, setResolveNotes] = useState("");
  const [escalateTransmission, setEscalateTransmission] = useState("");
  const [resolveLoading, setResolveLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [alertsRes, patientsRes, todayRes, oralRes, proRes] = await Promise.all([
        api.get("/alerts?limit=20").catch(() => ({ data: { items: [] } })),
        api.get("/patients?limit=500").catch(() => ({ data: { items: [] } })),
        api.get("/appointments/ide/today").catch(() => ({ data: [] })),
        api.get("/oral-adherence/ide/patients").catch(() => ({ data: { items: [] } })),
        api.get("/studies/pro-followup?limit=1").catch(() => ({ data: { summary: {} } })),
      ]);
      setProFollowup(proRes.data?.summary || { totalOpen: 0, late: 0, dueNow: 0 });
      
      // Only show ACTIVE alerts (NEW, IN_PROGRESS, ESCALATED)
      const alertsList = getItems(alertsRes.data);
      const activeAlerts = alertsList.filter(a => 
        ["NEW", "IN_PROGRESS", "ESCALATED"].includes(a.status)
      );
      setAlerts(activeAlerts.slice(0, 8));

      setTodayAppts(Array.isArray(todayRes.data) ? todayRes.data : getItems(todayRes.data));
      setOralPatients(oralRes.data?.items || []);
      
      // Categorize patients
      const patientsList = getItems(patientsRes.data);
      const now = new Date();
      
      const categories = {
        noCheckin: [],
        critical: [],
        toMonitor: [],
        hospitalized: []
      };
      
      patientsList.forEach(p => {
        const checkinExpectation = getScheduledCheckinExpectation(p, p.lastCheckinAt, now);
        const hasNoRecentCheckin = p.dossierStatus !== "hospitalise" && checkinExpectation.isMissing;
        
        if (hasNoRecentCheckin) {
          categories.noCheckin.push({ ...p, checkinExpectation });
        }
        
        if (p.dossierStatus === "hospitalise") categories.hospitalized.push(p);
        switch (p.status) {
          case "CRITICAL":
            categories.critical.push(p);
            break;
          case "OPEN_ALERT":
          case "TO_MONITOR":
            categories.toMonitor.push(p);
            break;
          default:
            break;
        }
      });
      
      setPatientsByCategory(categories);
      
      // Mark alerts as seen
      api.post("/kpi/notifications/mark-seen?notification_type=alerts").catch(() => {});
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // WebSocket listener for real-time alerts
  useEffect(() => {
    const unsubscribe = on("new_alert", () => {
      fetchData();
    });
    return () => unsubscribe?.();
  }, [on]);

  const handleAssignAlert = async (alertId) => {
    try {
      await api.patch(`/alerts/${alertId}/assign`);
      toast.success("Alerte prise en charge");
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de l'assignation");
    }
  };

  const handleOpenResolveModal = (alert) => {
    setShowResolveModal(alert);
    setResolveAction("resolve");
    setResolveOutcome("");
    setResolveNotes("");
    setEscalateTransmission("");
  };

  const handleSubmitResolve = async () => {
    if (resolveAction === "resolve" && !resolveOutcome) {
      toast.error("Veuillez sélectionner l'issue de l'alerte");
      return;
    }
    if (resolveAction === "escalate" && (!escalateTransmission || escalateTransmission.trim().length < 10)) {
      toast.error("La transmission est obligatoire (minimum 10 caractères)");
      return;
    }

    setResolveLoading(true);
    try {
      if (resolveAction === "resolve") {
        await api.patch(`/alerts/${showResolveModal.id}/resolve`, {
          resolutionOutcome: resolveOutcome,
          notes: resolveNotes,
        });
        toast.success("Alerte résolue");
      } else {
        const res = await api.patch(`/alerts/${showResolveModal.id}/escalate`, {
          transmission: escalateTransmission,
        });
        toast.success("Alerte escaladée au médecin");
        setEscalationResult({ ...res.data, patientId: showResolveModal.patientId });
      }
      setShowResolveModal(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'opération");
    } finally {
      setResolveLoading(false);
    }
  };

  const formatAlertReasons = (reasons) => {
    if (!reasons) return [];
    return reasons.map(r => r.replace(/_/g, " "));
  };

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="space-y-6">
          <div className="h-12 bg-slate-200 rounded-xl animate-pulse w-1/3" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="dash-card h-48 animate-pulse bg-slate-100" />
            ))}
          </div>
        </div>
      </ProfessionalLayout>
    );
  }

  const nurseTodayActions = buildNurseTodayActions({
    alerts,
    patientsByCategory,
    oralPatients,
    todayAppts,
    proFollowup,
  });

  return (
    <ProfessionalLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Bonjour, {user?.firstName}
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
            </p>
          </div>
          <button
            onClick={fetchData}
            data-testid="refresh-dashboard"
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 sm:py-2.5 text-sm text-slate-600 hover:text-slate-800 bg-white rounded-xl border border-slate-200/60 font-medium shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 min-h-[46px]"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
            Actualiser
          </button>
        </div>

        <ProfessionalTodayActions
          title="À traiter aujourd’hui"
          subtitle="Priorités de coordination issues des alertes, suivis et rendez-vous"
          actions={nurseTodayActions}
          emptyLabel="Aucune action prioritaire pour le moment"
          testId="nurse-today-actions-panel"
        />

        <div className="grid grid-cols-3 gap-2 sm:hidden" data-testid="nurse-mobile-priority-strip">
          <NurseMobilePriorityTile icon={AlertTriangle} label="Alertes" value={alerts.length} tone="rose" />
          <NurseMobilePriorityTile icon={Clock} label="Relances" value={patientsByCategory.noCheckin.length} tone="amber" />
          <NurseMobilePriorityTile icon={Eye} label="Surveiller" value={patientsByCategory.toMonitor.length} tone="orange" />
        </div>

        {/* Sprint 6 — Bilans pré-chimio */}
        <PreChemoBilanEncart mode="ide" />

        <OralAdherenceNurseCard
          patients={oralPatients}
        />

        {/* Active Alerts Section */}
        <div className="dash-card border-l-4 border-rose-400">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-slate-800 flex items-center gap-3">
              <div className="p-2 bg-rose-50 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-rose-500" strokeWidth={2} />
              </div>
              Alertes actives
              {alerts.length > 0 && (
                <span className="bg-rose-100 text-rose-700 text-xs px-2 py-0.5 rounded-full font-medium">
                  {alerts.length}
                </span>
              )}
            </h2>
          </div>

          {alerts.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
              <p className="text-sm font-medium">Aucune alerte active</p>
            </div>
          ) : (
            <div className="space-y-2">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  data-testid={`alert-row-${alert.id}`}
                  className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-xl border ${
                    alert.level === "CRITICAL"
                      ? "bg-rose-50 border-rose-200"
                      : "bg-orange-50 border-orange-200"
                  }`}
                >
                  <div className="flex items-start sm:items-center gap-3 min-w-0">
                    <StatusBadge status={alert.patientStatus || (alert.level === "CRITICAL" ? "CRITICAL" : "OPEN_ALERT")} size="sm" />
                    <div>
                      <p className="font-medium text-slate-800 text-sm">
                        {alert.patientName || "Patient"}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatAlertReasons(alert.reasons)?.slice(0, 2).join(", ") || "Alerte"}
                      </p>
                      {alert.performedByName && (
                        <p
                          className="text-[11px] text-amber-700 mt-0.5 font-medium"
                          data-testid={`alert-performed-by-${alert.id}`}
                        >
                          Signalé par {alert.performedByName}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {alert.createdAt ? new Date(alert.createdAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : ""}
                    </span>
                    {alert.status === "NEW" ? (
                      <button
                        onClick={() => handleAssignAlert(alert.id)}
                        data-testid={`assign-alert-${alert.id}`}
                        className="w-full sm:w-auto text-sm bg-primary text-white px-3 py-2.5 sm:py-1.5 rounded-lg hover:bg-teal-700 font-medium transition-colors min-h-[44px]"
                      >
                        Prendre en charge
                      </button>
                    ) : (
                      <button
                        onClick={() => handleOpenResolveModal(alert)}
                        data-testid={`resolve-alert-${alert.id}`}
                        className="w-full sm:w-auto text-sm bg-emerald-600 text-white px-3 py-2.5 sm:py-1.5 rounded-lg hover:bg-emerald-700 font-medium transition-colors min-h-[44px]"
                      >
                        Résoudre
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Patient Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
          {/* Check-ins attendus manquants */}
          <PatientCategorySection
            title="Check-in attendu manquant"
            icon={Clock}
            patients={patientsByCategory.noCheckin}
            bgColor="bg-amber-50"
            iconColor="text-amber-600"
            borderColor="border-amber-400"
            emptyMessage="Aucun check-in attendu manquant"
            showLastCheckin={true}
          />

          {/* Critiques */}
          <PatientCategorySection
            title="Patients critiques"
            icon={AlertTriangle}
            patients={patientsByCategory.critical}
            bgColor="bg-rose-50"
            iconColor="text-rose-600"
            borderColor="border-rose-400"
            emptyMessage="Aucun patient critique"
          />

          {/* À surveiller */}
          <PatientCategorySection
            title="Patients à surveiller"
            icon={Eye}
            patients={patientsByCategory.toMonitor}
            bgColor="bg-orange-50"
            iconColor="text-orange-600"
            borderColor="border-orange-400"
            emptyMessage="Aucun patient à surveiller"
          />

          {/* Hospitalisés */}
          <PatientCategorySection
            title="Patients hospitalisés"
            icon={Building2}
            patients={patientsByCategory.hospitalized}
            bgColor="bg-slate-100"
            iconColor="text-slate-600"
            borderColor="border-slate-400"
            emptyMessage="Aucun patient hospitalisé"
          />
        </div>

        {/* Phase point 4 — RDV du jour de mes patients (IDE référent) */}
        <div className="dash-card border-l-4 border-emerald-400" data-testid="ide-today-appts">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-slate-800 flex items-center gap-3">
              <div className="p-2 bg-emerald-50 rounded-xl">
                <Clock className="w-5 h-5 text-emerald-600" strokeWidth={2} />
              </div>
              RDV du jour
              {todayAppts.length > 0 && (
                <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full font-medium">
                  {todayAppts.length}
                </span>
              )}
            </h2>
          </div>
          {todayAppts.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Clock className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p className="text-sm font-medium">Aucun RDV programmé aujourd&apos;hui pour vos patients</p>
            </div>
          ) : (
            <div className="space-y-2">
              {todayAppts.map((a) => {
                const time = a.date ? new Date(a.date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—";
                const typeLabel = {
                  consultation: "Consultation",
                  imagerie: "Imagerie",
                  analyses: "Analyses",
                  chimio_hdj: "Chimio HDJ",
                  radiotherapie: "Radiothérapie",
                }[a.type] || a.type || "RDV";
                return (
                  <Link
                    key={a.id}
                    to={`/nurse/patients/${a.patientId}`}
                    data-testid={`today-appt-${a.id}`}
                    className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors border border-transparent hover:border-slate-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="text-sm font-bold text-emerald-700 w-14">{time}</div>
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{a.patientName}</p>
                        <p className="text-xs text-slate-500">{typeLabel}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Quick link to all patients */}
        <div className="text-center flex flex-wrap items-center justify-center gap-4">
          <Link
            to="/nurse/patients"
            className="inline-flex items-center gap-2 text-sm text-primary hover:text-teal-700 font-medium"
          >
            <Users className="w-4 h-4" />
            Voir tous les patients →
          </Link>
          <Link
            to="/nurse/pro-followup"
            className="inline-flex items-center gap-2 text-sm text-amber-700 hover:text-amber-800 font-medium"
            data-testid="nurse-pro-followup-link"
          >
            <ClipboardList className="w-4 h-4" />
            Suivi questionnaires PRO →
          </Link>
        </div>
      </div>

      {/* Alert Resolution Modal */}
      <EscalationFollowupModal result={escalationResult} patientId={escalationResult?.patientId} onClose={() => setEscalationResult(null)} />
      {showResolveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b bg-slate-50">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Traiter l&apos;alerte
                </h2>
                <button onClick={() => setShowResolveModal(null)} className="p-2 hover:bg-white/50 rounded-lg">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              <p className="text-sm text-slate-600 mt-2">
                Patient: <span className="font-medium">{showResolveModal.patientName}</span>
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Action Choice */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">Action *</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setResolveAction("resolve")}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                      resolveAction === "resolve"
                        ? "border-emerald-500 bg-emerald-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <CheckCircle className={`w-6 h-6 ${resolveAction === "resolve" ? "text-emerald-600" : "text-slate-400"}`} />
                    <span className={`text-sm font-medium ${resolveAction === "resolve" ? "text-emerald-700" : "text-slate-600"}`}>
                      Résoudre
                    </span>
                  </button>
                  <button
                    onClick={() => setResolveAction("escalate")}
                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                      resolveAction === "escalate"
                        ? "border-rose-500 bg-rose-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <ArrowUpRight className={`w-6 h-6 ${resolveAction === "escalate" ? "text-rose-600" : "text-slate-400"}`} />
                    <span className={`text-sm font-medium ${resolveAction === "escalate" ? "text-rose-700" : "text-slate-600"}`}>
                      Escalader
                    </span>
                  </button>
                </div>
              </div>

              {/* Resolution Options (only if resolving) */}
              {resolveAction === "resolve" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">Issue de cette alerte *</label>
                  <div className="space-y-2">
                    {[
                      { value: "hospitalisation_necessaire", label: "Hospitalisation nécessaire", desc: "Le patient a dû être hospitalisé", color: "rose" },
                      { value: "hospitalisation_evitee", label: "Hospitalisation évitée", desc: "Situation gérée en ambulatoire", color: "emerald" },
                      { value: "simple_surveillance", label: "Simple surveillance", desc: "Alerte mineure, surveillance à domicile", color: "sky" },
                      { value: "erreur_declaration", label: "Erreur de déclaration", desc: "Fausse alerte vérifiée avec le patient — justification obligatoire", color: "slate" },
                    ].map(opt => {
                      const styles = getResolutionOutcomeStyles(opt.color);
                      return (
                      <button
                        key={opt.value}
                        onClick={() => setResolveOutcome(opt.value)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                          resolveOutcome === opt.value
                            ? styles.selected
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <div className={`p-2 rounded-lg ${resolveOutcome === opt.value ? styles.iconBg : "bg-slate-100"}`}>
                          {opt.color === "rose" ? <Hospital className="w-4 h-4 text-rose-600" /> :
                           opt.color === "emerald" ? <CheckCircle className="w-4 h-4 text-emerald-600" /> :
                           <Eye className="w-4 h-4 text-sky-600" />}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800 text-sm">{opt.label}</p>
                          <p className="text-xs text-slate-500">{opt.desc}</p>
                        </div>
                      </button>
                    );})}
                  </div>
                  
                  {/* Notes */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Notes (optionnel)</label>
                    <textarea
                      value={resolveNotes}
                      onChange={e => setResolveNotes(e.target.value)}
                      placeholder="Commentaires additionnels..."
                      rows={2}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                    />
                  </div>
                </div>
              )}

              {/* Escalation Transmission (only if escalating) */}
              {resolveAction === "escalate" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Transmission au médecin *
                  </label>
                  <textarea
                    value={escalateTransmission}
                    onChange={e => setEscalateTransmission(e.target.value)}
                    placeholder="Décrivez la situation et pourquoi l'intervention du médecin est nécessaire..."
                    rows={4}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                  />
                  <p className="text-xs text-slate-400 mt-1">Minimum 10 caractères</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowResolveModal(null)}
                  className="flex-1 py-2.5 border border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSubmitResolve}
                  disabled={resolveLoading}
                  data-testid="submit-resolve-alert"
                  className={`flex-1 py-2.5 text-white rounded-xl font-medium disabled:opacity-50 ${
                    resolveAction === "resolve" 
                      ? "bg-emerald-600 hover:bg-emerald-700" 
                      : "bg-rose-600 hover:bg-rose-700"
                  }`}
                >
                  {resolveLoading ? "..." : resolveAction === "resolve" ? "Résoudre" : "Escalader"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ProfessionalLayout>
  );
}

function NurseMobilePriorityTile({ icon: Icon, label, value, tone }) {
  const colors = {
    rose: "bg-rose-50 text-rose-700 border-rose-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    orange: "bg-orange-50 text-orange-700 border-orange-100",
  };
  return (
    <div className={`rounded-xl border p-3 ${colors[tone] || colors.rose}`} data-testid={`nurse-mobile-priority-${label.toLowerCase()}`}>
      <Icon className="w-4 h-4 mb-1" />
      <p className="text-[11px] font-semibold leading-tight">{label}</p>
      <p className="text-sm font-black leading-tight mt-0.5">{value}</p>
    </div>
  );
}

function buildNurseTodayActions({ alerts, patientsByCategory, oralPatients, todayAppts, proFollowup }) {
  const actions = [];
  const newAlerts = (alerts || []).filter(a => a.status === "NEW");
  const inProgressAlerts = (alerts || []).filter(a => ["IN_PROGRESS", "ESCALATED"].includes(a.status));
  const oralNeedsAttention = (oralPatients || []).filter(p => p.needsAttention);

  if (newAlerts.length > 0) {
    const first = newAlerts[0];
    actions.push({
      id: "new-alerts",
      icon: AlertTriangle,
      tone: "rose",
      label: "Prendre en charge les alertes",
      detail: `${first.patientName || "Patient"} en premier · ${newAlerts.length} nouvelle(s) alerte(s)`,
      count: newAlerts.length,
      to: first.patientId ? `/nurse/patients/${first.patientId}` : "/nurse/patients",
    });
  }

  if (inProgressAlerts.length > 0) {
    const first = inProgressAlerts[0];
    actions.push({
      id: "open-alerts",
      icon: Activity,
      tone: "amber",
      label: "Clôturer ou escalader les alertes ouvertes",
      detail: `${first.patientName || "Patient"} · suivi en cours`,
      count: inProgressAlerts.length,
      to: first.patientId ? `/nurse/patients/${first.patientId}` : "/nurse/patients",
    });
  }

  if ((patientsByCategory.noCheckin || []).length > 0) {
    const first = patientsByCategory.noCheckin[0];
    actions.push({
      id: "no-checkin",
      icon: Clock,
      tone: "amber",
      label: "Relancer les check-ins attendus",
      detail: `${first.firstName || "Patient"} ${first.lastName || ""} · ${formatCheckinExpectation(first.checkinExpectation)}`,
      count: patientsByCategory.noCheckin.length,
      to: "/nurse/patients",
    });
  }

  if (oralNeedsAttention.length > 0) {
    actions.push({
      id: "oral-adherence",
      icon: Pill,
      tone: "emerald",
      label: "Vérifier l’observance per os",
      detail: `${oralNeedsAttention[0].patientName || "Patient"} · prise(s) non cochée(s)/non prise(s)`,
      count: oralNeedsAttention.length,
      to: "/nurse/oral-adherence",
    });
  }

  if ((todayAppts || []).length > 0) {
    actions.push({
      id: "appointments",
      icon: Calendar,
      tone: "blue",
      label: "Préparer les rendez-vous du jour",
      detail: `${todayAppts[0].patientName || "Patient"} · prochain RDV`,
      count: todayAppts.length,
      to: todayAppts[0].patientId ? `/nurse/patients/${todayAppts[0].patientId}` : "/nurse/patients",
    });
  }

  const proOpen = Number(proFollowup?.totalOpen || 0);
  if (proOpen > 0) {
    const late = Number(proFollowup?.late || 0);
    actions.push({
      id: "pro-followup",
      icon: ClipboardList,
      tone: "slate",
      label: "Revoir les questionnaires PRO",
      detail: late > 0 ? `${late} en retard · ${proOpen - late} à échéance` : `${proOpen} à échéance`,
      count: proOpen,
      to: "/nurse/pro-followup",
    });
  }

  return actions.slice(0, 5);
}

function formatCheckinExpectation(expectation) {
  if (!expectation) return "check-in attendu";
  const dateLabel = expectation.expectedDate
    ? expectation.expectedDate.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })
    : "date attendue";
  const delayLabel = expectation.daysLate > 0 ? `retard J+${expectation.daysLate}` : "attendu aujourd’hui";
  return `${delayLabel} · ${expectation.frequencyLabel || "fréquence quotidienne"} · prévu le ${dateLabel}`;
}

function PatientCategorySection({
  title,
  icon: Icon,
  patients,
  bgColor,
  iconColor,
  borderColor,
  emptyMessage,
  showLastCheckin = false,
}) {
  return (
    <div className={`dash-card border-l-4 ${borderColor}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-slate-800 flex items-center gap-3">
          <div className={`p-2 ${bgColor} rounded-xl`}>
            <Icon className={`w-5 h-5 ${iconColor}`} strokeWidth={2} />
          </div>
          {title}
          {patients.length > 0 && (
            <span className={`${bgColor} ${iconColor} text-xs px-2 py-0.5 rounded-full font-medium`}>
              {patients.length}
            </span>
          )}
        </h2>
      </div>

      {patients.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-6">{emptyMessage}</p>
      ) : (
        <div className="space-y-2">
          {patients.slice(0, 6).map((p) => (
            <Link
              key={p.id}
              to={`/nurse/patients/${p.id}`}
              className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${bgColor} flex items-center justify-center ${iconColor} font-semibold text-sm`}>
                  {p.firstName?.[0]}{p.lastName?.[0]}
                </div>
                <div>
                  <p className="font-medium text-slate-800 text-sm">{p.firstName} {p.lastName}</p>
                  <p className="text-xs text-slate-500 truncate max-w-[180px]">{formatCancerDisplay(p)}</p>
                  {showLastCheckin && (
                    <p className="text-xs text-amber-600 mt-0.5" data-testid={`patient-missing-checkin-detail-${p.id}`}>
                      {p.lastCheckinAt
                        ? `Dernier: ${new Date(p.lastCheckinAt).toLocaleDateString("fr-FR")}`
                        : "Jamais de check-in"}
                      {p.checkinExpectation?.frequencyLabel ? ` · ${p.checkinExpectation.frequencyLabel}` : ""}
                    </p>
                  )}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-1 transition-all" />
            </Link>
          ))}
          {patients.length > 6 && (
            <Link to="/nurse/patients" className="block text-center text-sm text-primary hover:text-teal-700 py-2">
              Voir les {patients.length - 6} autres →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function OralAdherenceNurseCard({ patients }) {
  const displayed = (patients || []).slice(0, 4);
  const attentionCount = (patients || []).filter(p => p.needsAttention).length;
  return (
    <div className="dash-card border-l-4 border-emerald-400" data-testid="nurse-oral-adherence-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-slate-800 flex items-center gap-3">
          <div className="p-2 bg-emerald-50 rounded-xl">
            <Pill className="w-5 h-5 text-emerald-600" strokeWidth={2} />
          </div>
          Patients sous per os
          {patients.length > 0 && (
            <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full font-medium" data-testid="nurse-oral-adherence-count">
              {patients.length}
            </span>
          )}
        </h2>
        <Link to="/nurse/oral-adherence" className="text-xs font-bold text-emerald-700 hover:text-emerald-900" data-testid="nurse-oral-adherence-open-page">
          Ouvrir →
        </Link>
      </div>
      {displayed.length === 0 ? (
        <div className="text-center py-6 text-slate-400" data-testid="nurse-oral-adherence-empty">
          <CheckCircle className="w-9 h-9 mx-auto mb-2 text-emerald-400" />
          <p className="text-sm font-medium">Aucun patient sous traitement per os actif</p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-500" data-testid="nurse-oral-adherence-rule">
            {attentionCount} patient(s) prioritaire(s) : au moins une prise non cochée/non prise sur 7 jours.
          </p>
          {displayed.map((patient) => (
            <div key={patient.patientId} className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 p-3 rounded-xl border border-slate-200 bg-white" data-testid={`nurse-oral-adherence-row-${patient.patientId}`}>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={`/nurse/oral-adherence`} className="font-semibold text-slate-800 text-sm hover:text-emerald-700" data-testid={`nurse-oral-adherence-patient-link-${patient.patientId}`}>
                    {patient.patientName || "Patient"}
                  </Link>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border font-bold ${patient.needsAttention ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`} data-testid={`nurse-oral-adherence-status-${patient.patientId}`}>
                    {patient.needsAttention ? "À prioriser" : "À jour"}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1" data-testid={`nurse-oral-adherence-details-${patient.patientId}`}>
                  {(patient.treatments || []).map(t => t.molecule).filter(Boolean).join(" · ") || "Traitement per os"} · {patient.adherenceRate7d}% sur 7 j
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2" data-testid={`nurse-oral-adherence-actions-${patient.patientId}`}>
                {patient.patientPhone && (
                  <a href={`tel:${patient.patientPhone}`} className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 font-semibold flex items-center gap-1" data-testid={`nurse-oral-adherence-call-${patient.patientId}`}>
                    <Phone className="w-3 h-3" /> Appeler
                  </a>
                )}
                <Link to="/nurse/oral-adherence" className="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 font-semibold" data-testid={`nurse-oral-adherence-calendar-${patient.patientId}`}>
                  Calendrier
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
